// stores/course.js
import { defineStore } from "pinia";
import api from "@/services/api";

export const useCourseStore = defineStore("course", {
  state: () => ({
    courses: [],
    currentCourse: null,
    loading: false,
    error: null,
    creating: false,
  }),

  getters: {
    totalCourses: (state) => state.courses.length,
    publishedCourses: (state) => state.courses.filter((c) => c.isPublished),
    draftCourses: (state) => state.courses.filter((c) => !c.isPublished),
  },

  actions: {
    // Fetch all published courses (for public viewing)
    async fetchPublishedCourses(filters = {}) {
      try {
        this.loading = true;
        const response = await api.get("/courses", { params: filters });
        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to fetch courses");
        }
        return {
          success: true,
          courses: response.data.data,
          pagination: response.data.pagination,
        };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },
    // Fetch all courses for a creator (including drafts)
    async fetchCreatorCourses(creatorId) {
      try {
        this.loading = true;
        const response = await api.get(`/courses/creator/${creatorId}`);
        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to fetch courses");
        }
        this.courses = response.data.data;
        return { success: true, courses: this.courses };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Fetch a single course for editing (includes drafts)
    async fetchCourseForEdit(courseId) {
      try {
        this.loading = true;
        const response = await api.get(`/courses/${courseId}/edit`);
        if (!response.data.success) {
          throw new Error(response.data.error || "Course not found");
        }
        this.currentCourse = response.data.data;
        return { success: true, course: this.currentCourse };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Fetch a single published course (for public viewing)
    async fetchPublishedCourse(courseId) {
      try {
        this.loading = true;
        const response = await api.get(`/courses/${courseId}`);
        if (!response.data.success) {
          throw new Error(response.data.error || "Course not found");
        }
        this.currentCourse = response.data.data;
        return { success: true, course: this.currentCourse };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Fetch a single course for preview (drafts, creator only)
    async fetchCourseForPreview(courseId, creatorId) {
      try {
        this.loading = true;
        const response = await api.get(
          `/courses/creator/${creatorId}/course/${courseId}`,
        );
        if (!response.data.success) {
          throw new Error(response.data.error || "Course not found");
        }
        this.currentCourse = response.data.data;
        return { success: true, course: this.currentCourse };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },
    // Create a new course
    async createCourse(courseData) {
      if (this.creating) {
        console.warn("⏳ Course creation already in progress");
        return { success: false, error: "Already creating a course" };
      }

      try {
        this.creating = true;
        this.loading = true;

        const payload = {
          title: courseData.title || "New Course",
          description: courseData.description || "Describe your course...",
          category: courseData.category || "other",
          price: courseData.price || 0,
          currency: courseData.currency || "USD",
          difficulty: courseData.difficulty || "beginner",
          tags: courseData.tags || [],
          ...courseData,
        };

        const response = await api.post("/courses", payload);
        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to create course");
        }

        const newCourse = response.data.data;
        this.currentCourse = newCourse;
        this.courses.unshift(newCourse);

        return { success: true, course: newCourse };
      } catch (error) {
        console.error("Create course error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
        this.creating = false;
      }
    },
    // stores/course.js - Update the updateCourse method
    async updateCourse(courseId, updates) {
      try {
        this.loading = true;
        console.log(`📝 Updating course: ${courseId}`, updates);

        // Use the correct endpoint with courseId parameter
        const response = await api.put(`/courses/${courseId}`, updates);

        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to update course");
        }

        const updatedCourse = response.data.data;

        if (this.currentCourse?.id === courseId) {
          this.currentCourse = updatedCourse;
        }

        const index = this.courses.findIndex((c) => c.id === courseId);
        if (index !== -1) {
          this.courses[index] = updatedCourse;
        }

        console.log(`✅ Course updated successfully: ${courseId}`);
        return { success: true, data: updatedCourse };
      } catch (error) {
        console.error("Update course error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Also fix deleteCourse
    async deleteCourse(courseId) {
      try {
        this.loading = true;
        const response = await api.delete(`/courses/${courseId}`);

        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to delete course");
        }

        this.courses = this.courses.filter((c) => c.id !== courseId);
        if (this.currentCourse?.id === courseId) {
          this.currentCourse = null;
        }
        return { success: true };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Section management
    async addSection(courseId, sectionData) {
      try {
        const response = await api.post(
          `/courses/${courseId}/sections`,
          sectionData,
        );
        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to add section");
        }
        return { success: true, section: response.data.data };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    // Module management
    async addModule(courseId, sectionId, moduleData) {
      try {
        const response = await api.post(
          `/courses/${courseId}/sections/${sectionId}/modules`,
          moduleData,
        );
        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to add module");
        }
        return { success: true, module: response.data.data };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    resetState() {
      this.loading = false;
      this.creating = false;
      this.error = null;
    },
  },
});
